import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from '../AppIcon';
import Button from './Button';

const UserProfileDropdown = ({ user = null }) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);
  const navigate = useNavigate();

  const defaultUser = {
    name: 'SAP Architect',
    email: 'architect@enterprise.com',
    organization: 'Enterprise Solutions Ltd',
    role: 'Solution Architect',
    avatar: null
  };

  const currentUser = user || defaultUser;

  const menuItems = [
    {
      label: 'Profile Settings',
      icon: 'User',
      action: () => console.log('Navigate to profile'),
      divider: false
    },
    {
      label: 'Organization',
      icon: 'Building2',
      action: () => console.log('Navigate to organization'),
      divider: false
    },
    {
      label: 'Preferences',
      icon: 'Settings',
      action: () => console.log('Navigate to preferences'),
      divider: true
    },
    {
      label: 'Help & Support',
      icon: 'HelpCircle',
      action: () => console.log('Navigate to help'),
      divider: false
    },
    {
      label: 'Documentation',
      icon: 'BookOpen',
      action: () => console.log('Navigate to docs'),
      divider: true
    },
    {
      label: 'Sign Out',
      icon: 'LogOut',
      action: () => {
        console.log('Sign out');
        navigate('/user-login');
      },
      divider: false,
      destructive: true
    }
  ];

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef?.current && !dropdownRef?.current?.contains(event?.target)) {
        setIsOpen(false);
      }
    };

    const handleEscape = (event) => {
      if (event?.key === 'Escape') {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      document.addEventListener('keydown', handleEscape);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [isOpen]);

  const handleMenuItemClick = (action) => {
    action();
    setIsOpen(false);
  };

  const getInitials = (name) => {
    return name?.split(' ')?.map(word => word?.charAt(0))?.join('')?.toUpperCase()?.slice(0, 2);
  };

  return (
    <div className="relative" ref={dropdownRef}>
      {/* Profile Button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center space-x-3 p-2 rounded-lg hover:bg-muted nav-transition focus:outline-none focus:ring-2 focus:ring-ring"
        aria-expanded={isOpen}
        aria-haspopup="true"
      >
        <div className="flex items-center space-x-3">
          {/* Avatar */}
          <div className="w-8 h-8 bg-primary rounded-full flex items-center justify-center text-primary-foreground text-sm font-medium">
            {currentUser?.avatar ? (
              <img 
                src={currentUser?.avatar} 
                alt={currentUser?.name}
                className="w-full h-full rounded-full object-cover"
              />
            ) : (
              getInitials(currentUser?.name)
            )}
          </div>
          
          {/* User Info - Hidden on mobile */}
          <div className="hidden md:block text-left">
            <div className="text-sm font-medium text-foreground">
              {currentUser?.name}
            </div>
            <div className="text-xs text-muted-foreground">
              {currentUser?.role}
            </div>
          </div>
        </div>
        
        <Icon 
          name="ChevronDown" 
          size={16} 
          className={`text-muted-foreground nav-transition ${
            isOpen ? 'rotate-180' : ''
          }`}
        />
      </button>
      {/* Dropdown Menu */}
      {isOpen && (
        <div className="absolute right-0 top-full mt-2 w-64 bg-popover border border-border rounded-lg shadow-elevation-2 z-60 animate-fade-in">
          {/* User Info Header */}
          <div className="p-4 border-b border-border">
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 bg-primary rounded-full flex items-center justify-center text-primary-foreground font-medium">
                {currentUser?.avatar ? (
                  <img 
                    src={currentUser?.avatar} 
                    alt={currentUser?.name}
                    className="w-full h-full rounded-full object-cover"
                  />
                ) : (
                  getInitials(currentUser?.name)
                )}
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-sm font-medium text-popover-foreground truncate">
                  {currentUser?.name}
                </div>
                <div className="text-xs text-muted-foreground truncate">
                  {currentUser?.email}
                </div>
                <div className="text-xs text-muted-foreground truncate">
                  {currentUser?.organization}
                </div>
              </div>
            </div>
          </div>

          {/* Menu Items */}
          <div className="py-2">
            {menuItems?.map((item, index) => (
              <React.Fragment key={index}>
                <button
                  onClick={() => handleMenuItemClick(item?.action)}
                  className={`w-full flex items-center space-x-3 px-4 py-2 text-left hover:bg-muted nav-transition ${
                    item?.destructive 
                      ? 'text-destructive hover:text-destructive' :'text-popover-foreground'
                  }`}
                >
                  <Icon 
                    name={item?.icon} 
                    size={16} 
                    className="text-current flex-shrink-0"
                  />
                  <span className="text-sm">{item?.label}</span>
                </button>
                {item?.divider && (
                  <div className="my-1 border-t border-border" />
                )}
              </React.Fragment>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default UserProfileDropdown;